// Where the projected COD comes from, in one line: the specialists' achievable COD, or the
// page's plan summed from installation start with the rule that sizes installation. The
// milestone table's COD note and CodBasis both read it.
import type { ReactNode } from "react";

import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { day, daysBetween, formatDate } from "./dates";
import { stepById } from "./pathSteps";
import { installLength, type Projection, type Scheduled } from "./schedule";

/** Who recorded the specialists' achievable COD, and when. */
export const recordedBy = (b: AmazonBuilding): string => {
  const basis = b.achievable_cod_basis;
  return basis?.by && basis.set_at
    ? `${basis.by} recorded on ${formatDate(day(basis.set_at))}`
    : "the specialists recorded";
};

/** One line on the projected COD's source: the specialists' date, or the plan's sum from
 * installation start naming the rule that sizes installation. Null when the plan has
 * nothing to time installation by. */
export const codLine = (
  b: AmazonBuilding,
  plan: Record<string, Scheduled>,
  projection: Projection,
): ReactNode => {
  if (!projection.date) return null;
  if (projection.bySpecialists) {
    return `The specialists' achievable COD: the earliest date ${recordedBy(b)} as possible.`;
  }
  const install = installLength(b);
  if (!install || !plan.install.start) return null;
  const connect = stepById("commission").days ?? 0;
  const total = daysBetween(plan.install.start, projection.date);
  const fit = Math.round(install.days);
  const rule =
    install.scheduleGap != null
      ? `Schedule 1's ${install.scheduleGap} days from installation start to the target COD`
      : install.basis;
  const wait =
    plan.commission.winter ??
    plan.install.winter ??
    (total > fit + connect ? "Connecting waits for the grid offer." : "");
  return (
    <>
      {total} days after installation start ({formatDate(plan.install.start)}):{" "}
      <b>{rule}</b>, so {fit} days to install, then {connect} to connect and commission.
      {wait ? ` ${wait}` : ""}
    </>
  );
};
